/**
 * Stale code-split chunks after a deploy.
 *
 * Every deploy renames the code-split chunks and `prerender-shells` prunes
 * `/var/www/uztender/releases` to the newest three. A tab opened BEFORE a deploy
 * still holds the OLD HTML, so the moment its visitor navigates to a lazy route
 * the browser asks for a bundle that no longer exists, the dynamic import
 * rejects, and react-router hands the failure to the route `errorElement` —
 * the «Sahifa ochilmadi» screen. Visitors read that as "I cannot log in",
 * because the code-split routes are exactly the signed-in ones (dashboard,
 * favorites, billing-return, invoice).
 *
 * Measured on prod 2026-09-15: three deploys in one day (11:46 / 12:51 / 14:31
 * Tashkent), 38 visitors carried a live session across one of them and 28 of
 * those were signed in; `dashboard-Cy4BLmFc.js`, `favorites-C5ZmhkEz.js`,
 * `billing-return-C_DGn9B1.js` and `invoice-DElVFKRG.js` all answered 404 while
 * their freshly-hashed twins answered 200.
 *
 * ⚠️ None of this was visible in telemetry. The rejection is caught by the
 * router boundary and never reaches `window.onerror`, so PostHog's `$exception`
 * holds not a single row for it — which is why this recovers AND reports.
 *
 * The recovery is one reload: the fresh HTML names the new hashes and the
 * navigation completes.
 */
import { track } from './funnel';

const RELOAD_FLAG = 'uzt:chunk-reload';

/** `sessionStorage` throws in some privacy modes — never let that break a route. */
function readFlag(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    return null;
  }
}

function writeFlag(value: string): boolean {
  try {
    sessionStorage.setItem(RELOAD_FLAG, value);
    return true;
  } catch {
    return false;
  }
}

function clearFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* no-op */
  }
}

/**
 * Wrap a `React.lazy` loader so a stale-chunk rejection reloads the page once.
 *
 * ⚠️ The flag is cleared ONLY after a chunk actually loads, never on boot. An
 * earlier draft cleared it while reporting at startup, which re-armed the retry
 * before the failing import ran again: a genuinely broken chunk would then
 * reload forever. Clearing on success keeps the budget at one reload per broken
 * load while still re-arming for a LATER deploy in the same session.
 *
 * The rejection path returns a promise that never settles: the document is
 * being replaced, and resolving would let React render a module that is about
 * to be thrown away.
 */
export function lazyRoute<T>(route: string, load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().then(
      (mod) => {
        const stamp = readFlag();
        if (stamp) {
          clearFlag();
          // We got here BECAUSE the previous attempt 404'd and we reloaded —
          // this is the recovery, and the only moment it can be reported.
          const started = Number(stamp);
          track('chunk_reload_recovered', {
            route,
            ms: Number.isFinite(started) ? Math.max(0, Date.now() - started) : null,
          });
        }
        return mod;
      },
      (err: unknown) => {
        if (typeof window === 'undefined') throw err;
        // Already retried in this tab: the chunk is broken, not merely stale.
        if (readFlag()) throw err;
        // No storage → no way to make the retry single-shot, and a reload loop
        // is worse than an error screen.
        if (!writeFlag(String(Date.now()))) throw err;
        window.location.reload();
        return new Promise<T>(() => {});
      },
    );
}
